import { motion } from "framer-motion";
import { Globe, ExternalLink, Link2Off } from "lucide-react";
import { Button } from "../components/ui/button";
import StatusBadge from "./StatusBadge";
import { ConnectionBadge } from "./connectionStatus";

export default function BusinessCard({ business, index = 0, connection }) {
  const hasOs = business.os_url && business.os_url.trim().length > 0;
  const hasSite = business.public_url && business.public_url.trim().length > 0;
  const testId = business.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.06 }}
      className="group h-full"
      data-testid={`business-card-${testId}`}
    >
      <div className="flex flex-col h-full bg-white border border-gray-200 rounded-xl p-6 hover:-translate-y-1 hover:shadow-lg hover:border-[#C4B5FD] transition-[transform,box-shadow,border-color] duration-200">
        <div className="flex items-start justify-between mb-5">
          <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-[#581C87] to-[#7C3AED] flex items-center justify-center text-white font-heading font-bold text-lg shrink-0">
            {business.logo || business.name.slice(0, 2).toUpperCase()}
          </div>
          <StatusBadge status={business.status} />
        </div>

        <h3 className="font-heading font-bold text-lg tracking-tight text-gray-900 leading-snug">
          {business.name}
        </h3>
        <p className="mt-2 text-sm text-gray-500 leading-relaxed flex-grow">
          {business.description}
        </p>
        {business.os_note && (
          <p className="mt-2 text-xs text-[#6B21A8]" data-testid={`os-note-${testId}`}>{business.os_note}</p>
        )}

        <div className="mt-5 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500 font-mono">
          <span className="flex items-center gap-2"><Globe className="h-3.5 w-3.5" />{business.domain || "no domain set"}</span>
          {connection && <ConnectionBadge status={connection} testId={`card-conn-status-${testId}`} />}
        </div>

        <div className="mt-5 pt-5 border-t border-gray-100 space-y-2">
          {hasOs ? (
            <Button asChild className="w-full bg-[#581C87] hover:bg-[#6B21A8] text-white" data-testid={`open-os-button-${testId}`}>
              <a href={business.os_url} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4 mr-2" />
                Open Operating System
              </a>
            </Button>
          ) : (
            <Button disabled className="w-full bg-gray-100 text-gray-400 cursor-not-allowed hover:bg-gray-100" data-testid={`os-not-configured-button-${testId}`}>
              <Link2Off className="h-4 w-4 mr-2" />
              Operating System Not Configured
            </Button>
          )}
          {hasSite ? (
            <Button asChild variant="outline" className="w-full border-gray-200 hover:border-[#581C87] hover:text-[#581C87]" data-testid={`visit-website-button-${testId}`}>
              <a href={business.public_url} target="_blank" rel="noopener noreferrer">
                <Globe className="h-4 w-4 mr-2" />
                Visit Website
              </a>
            </Button>
          ) : (
            <p className="text-center text-xs text-gray-400 py-1.5" data-testid={`no-website-${testId}`}>No public website configured</p>
          )}
        </div>
      </div>
    </motion.div>
  );
}
